import { CourseAdditionalFields } from '@app/enums/course-additional-fields.enum';
import { environment } from '@environments/environment';
import { AdditionalField } from './additional-field.model';
import { Sessions } from './sessions.model';
import { VoucherPageObject } from './voucher-page-object.model';

/**
 * Course model
 * Courses contain a collection of addtional fields and sessions
 */
export class Course {
  constructor(
    public subscriptionId: number, // Parent Subscription Id
    public subscriptionSlugName: string, // Parent Subscription Slug Name
    public id: number,
    public name: string,
    public code: string,
    public description: string,
    public type: string,
    public duration: number,
    public additionalFields: AdditionalField[],
    public sessions: Sessions[],
    public courseSelected: boolean = false,
    public slugName: string,
    public enrolled: boolean,
    public relatedCourseIds?: number[],
    public relatedSubscriptionIds?: number[],
    public url?: string,
    public voucherPageObject?: VoucherPageObject,
    public enrolledSession?: Sessions[]
  ) {}

  /**
   * Return an additional field value
   * @param id - id or additional field value
   * @returns the course additional field value by given id
   */
  getAdditionalFieldValue(id: number): any {
    let value = '';
    this.additionalFields &&
      this.additionalFields.forEach((field) => {
        // NOTE: Allow == type coercion, DO NOT use === coercion
        // eslint-disable-next-line
        if (field && field.id == id) {
          value = field.value;
        }
      });
    return value;
  }

  /**
   * Return course duration for ILT/VILT courses in days from Additional Fields
   * NOTE: duration maybe empty so use this.
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get courseDuration(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.COURSE_DURATION_DAYS);
  }

  /**
   * Return course duration for eLearning Courses in mins from Additional Fields
   * NOTE: duration maybe empty so use this.
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get courseDurationMins(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.COURSE_DURATION_MINS);
  }

  /**
   * Returns course version requirement from additional fields
   * NOTE: versionRequirement maybe empty so use this.
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get courseVersionRequirement(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.SOFTWARE_VERSION_REQUIREMENT);
  }

  /**
   * Returns course target audience from additional fields
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get targetAudience(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.TARGET_AUDIENCE);
  }

  /**
   * Returns course pre-requisites SS(?) form additional fields
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get prerequisitesSS(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.COURSE_PRE_REQUISITES_SS);
  }

  /**
   * Returns true or false if course is free. It is considered free if IsFreeCourse is true from
   * Docebo. Else if it is false, then this course requires a subscription.
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get isFreeCourse(): boolean {
    const isFreeCourse: string = this.getAdditionalFieldValue(
      CourseAdditionalFields.IS_FREE_COURSE
    );
    return isFreeCourse && isFreeCourse.toLowerCase() === 'true' ? true : false;
  }

  /**
   * Returns course pre-requisites form additional fields
   * @returns - returns an array of any objects which can be an html or json object from Docebo
   */
  get prerequisites(): any[] {
    const fields = environment.docebo.additional_fields;
    const preReqFieldId = fields && fields.prereqs;
    const prereqs = preReqFieldId && this.getAdditionalFieldValue(preReqFieldId);
    let prereqsArr = [];

    if (!!prereqs) {
      const value: any = prereqs.replace(/(<([^>]+)>)/gi, '');
      prereqsArr = value.split(',');
    }

    return prereqsArr;
  }

  /**
   * Returns the course instruction type from additional fields.
   * NOTE: The course type is not the same as this instruction type.
   */
  get instructionType(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.INSTRUCTION_TYPE);
  }

  /**
   * Return course learning type provided in the additional fields in the environment file.
   * NOTE: This is NOT the type from the course.
   * @returns returns an object which can be an html or json object from Docebo
   */
  getLearningType(): any {
    let type;
    const fields = environment.docebo.additional_fields;
    const learningtypeFieldId = fields && fields.learning_type;

    this.additionalFields &&
      this.additionalFields.forEach((field) => {
        // NOTE: Allow == type coercion, DO NOT use === coercion
        // eslint-disable-next-line
        if (field && field.id == learningtypeFieldId) {
          if (!!field.value) {
            type = field.value;
          }
        }
      });

    return type;
  }
  /**
   * Returns course objectives from additional fields
   * @returns - returns any object which can be an html or json object from Docebo
   */
  get objectives(): any {
    return this.getAdditionalFieldValue(CourseAdditionalFields.COURSE_OBJECTIVES);
  }
}
